// EmployeeBookingFilterBar.jsx
import React, { useState, useEffect } from "react";
import moment from "moment";

const previousBookingsStatusOptions = [
  "Pending",
  "Confirmed",
  "Headed",
  "Reached",
  "Started",
  "Scanned",
  "Completed",
  "Cancelled",
  "Declined",
];

const previousBookingDatePresets = [
  { label: "Last 7 Days", start: moment().subtract(7, "days"), end: moment() },
  { label: "Last Month", start: moment().subtract(1, "month"), end: moment() },
  { label: "Last Year", start: moment().subtract(1, "year"), end: moment() },
  { label: "Overall", start: null, end: null },
  { label: "Custom", start: null, end: null },
];

const verificationStatusOptions = [
  { label: "All", value: null },
  { label: "PENDING", value: "PENDING" },
  {
    label: "PAYMENT_VERIFICATION_PENDING",
    value: "PAYMENT_VERIFICATION_PENDING",
  },
  { label: "SCAN_VERIFICATION_PENDING", value: "SCAN_VERIFICATION_PENDING" },
  { label: "VERIFIED", value: "VERIFIED" },
];

const EmployeeBookingFilterBar = ({ onFilterChange, currentFilter = {} }) => {
  const defaultStatuses = previousBookingsStatusOptions;
  const datePresets = previousBookingDatePresets;

  // ---------- INITIAL VALUES (only on mount) ----------
  const initialStatuses = Array.isArray(currentFilter.status)
    ? currentFilter.status
    : typeof currentFilter.status === "undefined"
    ? defaultStatuses
    : [];

  const initialSelectedPreset =
    (currentFilter.selectedDatePreset &&
      datePresets.find((p) => p.label === currentFilter.selectedDatePreset)) ||
    datePresets[0];

  const initialCustomStart = currentFilter.customStart ?? "";
  const initialCustomEnd = currentFilter.customEnd ?? "";
  const initialVerificationStatus = currentFilter.verificationStatus ?? null;

  // ---------- LOCAL STATE ----------
  const [selectedStatuses, setSelectedStatuses] = useState(initialStatuses);
  const [selectedDatePreset, setSelectedDatePreset] = useState(
    initialSelectedPreset
  );
  const [customStart, setCustomStart] = useState(initialCustomStart);
  const [customEnd, setCustomEnd] = useState(initialCustomEnd);
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState(
    initialVerificationStatus
  );

  // ---------- PROPAGATE FILTER TO PARENT ----------
  useEffect(() => {
    const isCustom = selectedDatePreset?.label === "Custom";

    // For "Custom": don't propagate until BOTH dates are selected
    if (isCustom && (!customStart || !customEnd)) {
      return;
    }

    const startDate =
      isCustom && customStart
        ? customStart
        : selectedDatePreset?.start?.format?.("YYYY-MM-DD") || null;

    const endDate =
      isCustom && customEnd
        ? customEnd
        : selectedDatePreset?.end?.format?.("YYYY-MM-DD") || null;

    onFilterChange({
      ...currentFilter,
      status:
        selectedStatuses && selectedStatuses.length ? selectedStatuses : null,
      queryStartDate: startDate,
      queryEndDate: endDate,
      verificationStatus: selectedVerificationStatus ?? null,
      isCustomDateRange: isCustom,
      selectedDatePreset: selectedDatePreset?.label || null,
      customStart: isCustom ? customStart : null,
      customEnd: isCustom ? customEnd : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStatuses,
    selectedDatePreset,
    customStart,
    customEnd,
    selectedVerificationStatus,
  ]);

  // ---------- HANDLERS ----------
  const handleStatusToggle = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleClearAllStatuses = () => {
    setSelectedStatuses([]);
  };

  const handleResetFilters = () => {
    const preset0 = datePresets[0];
    setSelectedStatuses(defaultStatuses);
    setSelectedVerificationStatus(null);
    setSelectedDatePreset(preset0);
    setCustomStart("");
    setCustomEnd("");
  };

  // ---------- RENDER ----------
  return (
    <div className="flex flex-wrap gap-4 items-center p-4 bg-black border-b border-gray-200 rounded-t-2xl">
      {/* Status multi-select */}
      <div className="flex flex-col text-gray-900">
        <label className="mb-1 text-sm">Status</label>
        <div className="flex flex-wrap gap-2 max-w-[420px]">
          {previousBookingsStatusOptions.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => handleStatusToggle(status)}
              className={`px-2 py-1 border rounded-full text-xs sm:text-sm ${
                selectedStatuses.includes(status)
                  ? "bg-[#7F58E6] text-white border-[#7F58E6]"
                  : "border-[#7F58E6] text-[#7F58E6]"
              }`}
            >
              {status}
            </button>
          ))}
          <button
            type="button"
            onClick={handleClearAllStatuses}
            className="px-2 py-1 border border-red-400 rounded-full text-xs sm:text-sm text-red-400"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Verification Status */}
      <div className="flex flex-col text-gray-900">
        <label className="mb-1 text-sm">Verification Status</label>
        <select
          className="px-2 py-1 bg-white border-b border-[#E0E0E0] outline-none text-xs sm:text-sm"
          value={selectedVerificationStatus ?? ""}
          onChange={(e) =>
            setSelectedVerificationStatus(
              e.target.value === "" ? null : e.target.value
            )
          }
        >
          {verificationStatusOptions.map((opt) => (
            <option key={String(opt.value ?? "")} value={opt.value ?? ""}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Date Preset */}
      <div className="flex flex-col text-gray-900">
        <label className="mb-1 text-sm">Date Range</label>
        <select
          className="px-2 py-1 bg-white border-b border-[#E0E0E0] outline-none text-xs sm:text-sm"
          value={selectedDatePreset?.label}
          onChange={(e) => {
            const preset = datePresets.find((p) => p.label === e.target.value);
            setSelectedDatePreset(preset);
          }}
        >
          {datePresets.map((preset) => (
            <option key={preset.label} value={preset.label}>
              {preset.label}
            </option>
          ))}
        </select>
      </div>

      {/* Custom date range */}
      {selectedDatePreset?.label === "Custom" && (
        <div className="flex items-end gap-2">
          <div className="flex flex-col text-gray-900">
            <label className="mb-1 text-sm">Start Date</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-2 py-1 bg-white border-b border-[#E0E0E0] outline-none text-gray-900 text-xs sm:text-sm"
            />
          </div>
          <div className="flex flex-col text-gray-900">
            <label className="mb-1 text-sm">End Date</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-2 py-1 bg-white border-b border-[#E0E0E0] outline-none text-gray-900 text-xs sm:text-sm"
            />
          </div>
        </div>
      )}

      {/* 🔄 Reset filters */}
      <div className="flex flex-col text-gray-900 ml-auto">
        <label className="mb-1 text-sm">&nbsp;</label>
        <button
          type="button"
          onClick={handleResetFilters}
          className="px-3 py-1 border border-[#E0E0E0] rounded-full text-xs sm:text-sm text-[#7F58E6]"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
};

export default EmployeeBookingFilterBar;
